import { useState } from "react";
import { CreditCard, CheckCircle2, Clock, AlertCircle, Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import api from "../../../services/api";

var STATUS_MAP = {
  pendente: { cls: "bg-amber-100 text-amber-700", label: "Pendente", icon: Clock },
  pago: { cls: "bg-emerald-100 text-emerald-700", label: "Pago", icon: CheckCircle2 },
  atrasado: { cls: "bg-red-100 text-red-700", label: "Atrasado", icon: AlertCircle },
  vencido: { cls: "bg-red-100 text-red-700", label: "Vencido", icon: AlertCircle },
  cancelado: { cls: "bg-gray-200 text-gray-600", label: "Cancelado", icon: AlertCircle },
};

function formatCurrency(v) {
  if (v == null || v === 0) return "R$ 0";
  return "R$ " + Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
}

function formatDate(d) {
  if (!d) return "-";
  try {
    if (d.includes("-")) {
      var parts = d.split("-");
      return parts[2] + "/" + parts[1] + "/" + parts[0];
    }
    return new Date(d).toLocaleDateString("pt-BR");
  } catch(e) { return d; }
}

function isOverdue(dueDate, status) {
  if (status !== "pendente" || !dueDate) return false;
  try {
    var today = new Date(); today.setHours(0,0,0,0);
    var due = new Date(dueDate + "T00:00:00"); due.setHours(0,0,0,0);
    return due < today;
  } catch(e) { return false; }
}

function AddPaymentModal({ clientId, clientName, clientEmail, open, onClose, onSaved }) {
  var formState = useState({
    description: "",
    value: "",
    due_date: "",
    payment_method: "boleto",
    product_type: "consorcio",
  });
  var form = formState[0];
  var savingState = useState(false);
  var errorState = useState(null);

  function handleChange(field, value) {
    formState[1](function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  async function handleSave() {
    if (!form.description || !form.value || !form.due_date) {
      errorState[1]("Preencha todos os campos obrigatorios");
      return;
    }
    savingState[1](true);
    errorState[1](null);
    try {
      await api.post("/payments", {
        client_id: clientId,
        client_name: clientName,
        client_email: clientEmail || "",
        description: form.description,
        value: parseFloat(form.value),
        due_date: form.due_date,
        payment_method: form.payment_method,
        product_type: form.product_type,
      });
      onSaved();
    } catch(err) {
      errorState[1](err.response?.data?.detail || "Erro ao salvar");
    } finally {
      savingState[1](false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A2F3D]">
            <CreditCard className="w-5 h-5 text-amber-500" />Registrar Pagamento
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-2" data-testid="add-payment-modal">
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">Descricao *</label>
            <input type="text" value={form.description} onChange={function(e) { handleChange("description", e.target.value); }}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              placeholder="Ex: Parcela consorcio Jan/2026" data-testid="add-payment-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Valor (R$) *</label>
              <input type="number" step="0.01" value={form.value} onChange={function(e) { handleChange("value", e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                data-testid="add-payment-value" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Vencimento *</label>
              <input type="date" value={form.due_date} onChange={function(e) { handleChange("due_date", e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                data-testid="add-payment-due-date" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Metodo</label>
              <select value={form.payment_method} onChange={function(e) { handleChange("payment_method", e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                data-testid="add-payment-method">
                <option value="boleto">Boleto</option>
                <option value="cartao_credito">Cartao de Credito</option>
                <option value="debito_automatico">Debito Automatico</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Produto</label>
              <select value={form.product_type} onChange={function(e) { handleChange("product_type", e.target.value); }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400/40"
                data-testid="add-payment-product">
                <option value="consorcio">Consorcio</option>
                <option value="seguro">Seguro</option>
                <option value="plano_saude">Plano de Saude</option>
              </select>
            </div>
          </div>
          {errorState[0] && <p className="text-xs text-red-600 bg-red-50 px-3 py-2 rounded-lg">{errorState[0]}</p>}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={savingState[0]}
              className="px-4 py-2 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              data-testid="add-payment-save">
              {savingState[0] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}Salvar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function PaymentsSection({ payments, clientId, clientName, clientEmail, onRefresh }) {
  var showAddState = useState(false);

  var paidCount = (payments || []).filter(function(p) { return p.status === "pago"; }).length;
  var totalCount = (payments || []).length;
  var overdueCount = (payments || []).filter(function(p) { return isOverdue(p.due_date, p.status); }).length;
  var pctPaid = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

  function handleSaved() {
    showAddState[1](false);
    if (onRefresh) onRefresh();
  }

  if (!payments || payments.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6" data-testid="profile-payments-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-[#0A2F3D] flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-500" />Historico de Pagamentos</h2>
          {clientId && (
            <button onClick={function() { showAddState[1](true); }}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
              data-testid="add-payment-btn">
              <Plus className="w-3.5 h-3.5" />Registrar
            </button>
          )}
        </div>
        <p className="text-sm text-gray-400 text-center py-6">Nenhum pagamento registrado</p>
        {showAddState[0] && (
          <AddPaymentModal clientId={clientId} clientName={clientName} clientEmail={clientEmail}
            open={showAddState[0]} onClose={function() { showAddState[1](false); }} onSaved={handleSaved} />
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6" data-testid="profile-payments-section">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-bold text-[#0A2F3D] flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-amber-500" />Historico de Pagamentos
          <span className="ml-2 text-xs font-normal text-gray-400">{payments.length} registro{payments.length !== 1 ? "s" : ""}</span>
        </h2>
        {clientId && (
          <button onClick={function() { showAddState[1](true); }}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors"
            data-testid="add-payment-btn">
            <Plus className="w-3.5 h-3.5" />Registrar
          </button>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-4" data-testid="payment-progress">
        <div className="flex items-center justify-between text-[10px] text-gray-500 mb-1">
          <span>{paidCount} pago{paidCount !== 1 ? "s" : ""} de {totalCount}</span>
          <div className="flex items-center gap-2">
            {overdueCount > 0 && <span className="text-red-600 font-medium">{overdueCount} vencido{overdueCount !== 1 ? "s" : ""}</span>}
            <span className="font-medium">{pctPaid}%</span>
          </div>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: pctPaid + "%" }} />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="profile-payments-table">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">Descricao</th>
              <th className="text-right py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">Valor</th>
              <th className="text-center py-2.5 px-3 text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Vencimento</th>
              <th className="text-center py-2.5 px-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Metodo</th>
              <th className="text-center py-2.5 px-3 text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody>
            {payments.map(function(p) {
              var overdue = isOverdue(p.due_date, p.status);
              var stInfo = overdue
                ? { cls: "bg-red-100 text-red-700", label: "Vencido", icon: AlertCircle }
                : (STATUS_MAP[p.status] || { cls: "bg-gray-100 text-gray-600", label: p.status, icon: Clock });
              var Icon = stInfo.icon;
              return (
                <tr key={p.id} className={"border-b border-gray-50 " + (overdue ? "bg-red-50/50" : "hover:bg-gray-50/50")} data-testid={"profile-payment-" + p.id}>
                  <td className="py-2.5 px-3 text-gray-700">{p.description || "-"}</td>
                  <td className="py-2.5 px-3 text-right font-medium text-[#0A2F3D]">{formatCurrency(p.value)}</td>
                  <td className={"py-2.5 px-3 text-center hidden sm:table-cell " + (overdue ? "text-red-600 font-medium" : "text-gray-500")}>{formatDate(p.due_date)}</td>
                  <td className="py-2.5 px-3 text-center text-gray-500 capitalize hidden md:table-cell">{p.payment_method || "-"}</td>
                  <td className="py-2.5 px-3 text-center">
                    <span className={"px-2 py-0.5 text-xs font-medium rounded-full inline-flex items-center gap-1 " + stInfo.cls}>
                      <Icon className="w-3 h-3" />{stInfo.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAddState[0] && (
        <AddPaymentModal clientId={clientId} clientName={clientName} clientEmail={clientEmail}
          open={showAddState[0]} onClose={function() { showAddState[1](false); }} onSaved={handleSaved} />
      )}
    </div>
  );
}
